import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { supabase } from '../../lib/supabase';
import ImageUpload from '../../components/ImageUpload';
import NotificationSettings from '../../components/NotificationSettings';
import { getProfileCompletion } from '../../utils/attendeeUtils';

const AttendeeProfilePage = () => {
  const { user, profile } = useAuth();
  const [form, setForm] = useState(null);
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [newInterest, setNewInterest] = useState('');
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deleteStep, setDeleteStep] = useState(0);
  const [toast, setToast] = useState(null);

  useEffect(() => {
    if (profile) {
      setForm({
        full_name: profile.full_name || '',
        church_name: profile.church_name || '',
        city: profile.city || '',
        state: profile.state || '',
        phone: profile.phone || '',
        photo_url: profile.photo_url || '',
        dietary_needs: profile.dietary_needs || '',
        accessibility_needs: profile.accessibility_needs || '',
        interests: profile.interests || [],
        notification_settings: profile.notification_settings || {},
      });
    }
  }, [profile]);

  const showToast = (type, message) => {
    setToast({ type, message });
    setTimeout(() => setToast(null), 3000);
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const { error } = await supabase
        .from('profiles')
        .update({
          full_name: form.full_name,
          church_name: form.church_name,
          city: form.city,
          state: form.state,
          phone: form.phone,
          photo_url: form.photo_url,
          dietary_needs: form.dietary_needs,
          accessibility_needs: form.accessibility_needs,
          interests: form.interests,
        })
        .eq('id', user.id);
      if (error) throw error;
      setEditing(false);
      showToast('success', 'Profile updated successfully!');
    } catch (error) {
      showToast('error', 'Failed to update profile: ' + error.message);
    } finally {
      setSaving(false);
    }
  };

  const addInterest = () => {
    const trimmed = newInterest.trim();
    if (trimmed && !form.interests.includes(trimmed)) {
      setForm({ ...form, interests: [...form.interests, trimmed] });
      setNewInterest('');
    }
  };

  const removeInterest = (interest) => {
    setForm({ ...form, interests: form.interests.filter(i => i !== interest) });
  };

  const handleDeleteAccount = async () => {
    if (deleteStep === 0) {
      setDeleteStep(1);
      return;
    }
    if (deleteStep === 1) {
      setDeleteStep(2);
      return;
    }
    if (deleteStep === 2) {
      setSaving(true);
      try {
        const { error } = await supabase
          .from('profiles')
          .delete()
          .eq('id', user.id);
        if (error) throw error;
        await supabase.auth.signOut();
        window.location.href = '/';
      } catch (error) {
        showToast('error', 'Failed to delete account: ' + error.message);
        setDeleteStep(0);
        setShowDeleteConfirm(false);
      } finally {
        setSaving(false);
      }
    }
  };

  if (!form) {
    return (
      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="animate-pulse space-y-4">
          <div className="h-8 bg-gray-200 dark:bg-gray-700 rounded w-48" />
          <div className="h-48 bg-gray-200 dark:bg-gray-700 rounded-xl" />
        </div>
      </div>
    );
  }

  const completion = getProfileCompletion(form);

  return (
    <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      {/* Toast */}
      {toast && (
        <div className={`fixed top-20 right-4 z-50 px-4 py-3 rounded-lg shadow-lg ${
          toast.type === 'success' ? 'bg-green-500 text-white' : 'bg-red-500 text-white'
        }`}>
          {toast.message}
        </div>
      )}

      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 dark:text-white">
            Profile
          </h1>
          <p className="text-gray-500 dark:text-gray-400 mt-1">
            Manage your personal information and preferences.
          </p>
        </div>
        {!editing ? (
          <button
            onClick={() => setEditing(true)}
            className="px-4 py-2 bg-primary text-white rounded-lg text-sm font-medium hover:bg-primary-dark transition-colors"
          >
            Edit Profile
          </button>
        ) : (
          <div className="flex gap-2">
            <button
              onClick={() => {
                setEditing(false);
                setForm({
                  full_name: profile.full_name || '',
                  church_name: profile.church_name || '',
                  city: profile.city || '',
                  state: profile.state || '',
                  phone: profile.phone || '',
                  photo_url: profile.photo_url || '',
                  dietary_needs: profile.dietary_needs || '',
                  accessibility_needs: profile.accessibility_needs || '',
                  interests: profile.interests || [],
                  notification_settings: profile.notification_settings || {},
                });
              }}
              className="px-4 py-2 bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded-lg text-sm font-medium hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors"
            >
              Cancel
            </button>
            <button
              onClick={handleSave}
              disabled={saving}
              className="px-4 py-2 bg-primary text-white rounded-lg text-sm font-medium hover:bg-primary-dark transition-colors disabled:opacity-50"
            >
              {saving ? 'Saving...' : 'Save'}
            </button>
          </div>
        )}
      </div>

      {/* Profile completion */}
      {completion.percentage < 100 && (
        <div className="bg-blue-50 dark:bg-blue-900/20 rounded-xl p-4 mb-6">
          <div className="flex items-center justify-between mb-2">
            <p className="text-sm font-medium text-blue-900 dark:text-blue-300">
              Profile {completion.percentage}% complete
            </p>
            <span className="text-xs text-blue-700 dark:text-blue-400">
              {completion.missing.length} fields missing
            </span>
          </div>
          <div className="w-full h-2 bg-blue-200 dark:bg-blue-800 rounded-full overflow-hidden">
            <div className="h-full bg-blue-600 rounded-full transition-all" style={{ width: `${completion.percentage}%` }} />
          </div>
          {editing && completion.missing.length > 0 && (
            <div className="flex flex-wrap gap-1.5 mt-2">
              {completion.missing.map(field => (
                <span key={field} className="px-2 py-0.5 text-xs bg-blue-100 dark:bg-blue-900/40 text-blue-700 dark:text-blue-300 rounded-full">
                  {field}
                </span>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Profile photo */}
      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-100 dark:border-gray-700 p-6 mb-6">
        <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Profile Photo</h2>
        {editing ? (
          <ImageUpload
            currentImage={form.photo_url}
            onUpload={(url) => setForm({ ...form, photo_url: url })}
            bucket="avatars"
          />
        ) : (
          <div className="flex items-center gap-4">
            {form.photo_url ? (
              <img src={form.photo_url} alt={form.full_name} className="w-20 h-20 rounded-full object-cover" />
            ) : (
              <div className="w-20 h-20 rounded-full bg-gray-200 dark:bg-gray-700 flex items-center justify-center">
                <span className="text-2xl font-bold text-gray-400">
                  {form.full_name?.charAt(0) || 'A'}
                </span>
              </div>
            )}
            <div>
              <p className="font-medium text-gray-900 dark:text-white">{form.full_name || 'Attendee'}</p>
              <p className="text-sm text-gray-500 dark:text-gray-400">{user?.email}</p>
            </div>
          </div>
        )}
      </div>

      {/* Personal information */}
      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-100 dark:border-gray-700 p-6 mb-6">
        <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Personal Information</h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          {[
            { label: 'Full Name', key: 'full_name', type: 'text' },
            { label: 'Church/Organization', key: 'church_name', type: 'text' },
            { label: 'City', key: 'city', type: 'text' },
            { label: 'State', key: 'state', type: 'text' },
            { label: 'Phone Number', key: 'phone', type: 'tel', placeholder: '(555) 123-4567' },
          ].map(field => (
            <div key={field.key}>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                {field.label}
              </label>
              {editing ? (
                <input
                  type={field.type}
                  value={form[field.key]}
                  onChange={e => setForm({ ...form, [field.key]: e.target.value })}
                  placeholder={field.placeholder}
                  className="w-full px-4 py-2 bg-gray-100 dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-lg text-gray-900 dark:text-white focus:ring-2 focus:ring-primary"
                />
              ) : (
                <p className="text-gray-900 dark:text-white">{form[field.key] || '—'}</p>
              )}
            </div>
          ))}
          <div className="sm:col-span-2">
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Dietary Needs
            </label>
            {editing ? (
              <input
                type="text"
                value={form.dietary_needs}
                onChange={e => setForm({ ...form, dietary_needs: e.target.value })}
                placeholder="e.g., Vegetarian, Gluten-free, Nut allergy"
                className="w-full px-4 py-2 bg-gray-100 dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-lg text-gray-900 dark:text-white focus:ring-2 focus:ring-primary"
              />
            ) : (
              <p className="text-gray-900 dark:text-white">{form.dietary_needs || '—'}</p>
            )}
          </div>
          <div className="sm:col-span-2">
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Accessibility Needs
            </label>
            {editing ? (
              <input
                type="text"
                value={form.accessibility_needs}
                onChange={e => setForm({ ...form, accessibility_needs: e.target.value })}
                placeholder="e.g., Wheelchair access, Sign language interpreter"
                className="w-full px-4 py-2 bg-gray-100 dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-lg text-gray-900 dark:text-white focus:ring-2 focus:ring-primary"
              />
            ) : (
              <p className="text-gray-900 dark:text-white">{form.accessibility_needs || '—'}</p>
            )}
          </div>
        </div>

        {/* Interests */}
        <div className="mt-6">
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
            Interests / Topics
          </label>
          <div className="flex flex-wrap gap-2 mb-3">
            {form.interests?.map((interest, index) => (
              <span
                key={index}
                className="inline-flex items-center px-3 py-1 rounded-full text-sm bg-primary/10 text-primary"
              >
                {interest}
                {editing && (
                  <button
                    onClick={() => removeInterest(interest)}
                    className="ml-2 text-primary hover:text-primary-dark"
                    aria-label={`Remove ${interest}`}
                  >
                    ×
                  </button>
                )}
              </span>
            ))}
            {form.interests?.length === 0 && !editing && (
              <span className="text-gray-500 dark:text-gray-400">No interests added yet</span>
            )}
          </div>
          {editing && (
            <div className="flex gap-2">
              <input
                type="text"
                value={newInterest}
                onChange={e => setNewInterest(e.target.value)}
                onKeyPress={e => e.key === 'Enter' && (e.preventDefault(), addInterest())}
                placeholder="Add an interest..."
                className="flex-1 px-4 py-2 bg-gray-100 dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-lg text-gray-900 dark:text-white focus:ring-2 focus:ring-primary"
              />
              <button
                onClick={addInterest}
                className="px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary-dark transition-colors"
              >
                Add
              </button>
            </div>
          )}
        </div>

        {/* Notification settings (only in edit mode) */}
        {editing && (
          <div className="mt-6 pt-6 border-t border-gray-200 dark:border-gray-700">
            <NotificationSettings
              userId={user.id}
              userType="attendee"
              settings={form.notification_settings}
              onSave={settings => setForm({ ...form, notification_settings: settings })}
            />
          </div>
        )}
      </div>

      {/* Secondary navigation */}
      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-100 dark:border-gray-700 p-6 mb-6">
        <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Account & Preferences</h2>
        <div className="space-y-2">
          <Link to="/settings" className="flex items-center justify-between p-3 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors">
            <div className="flex items-center gap-3">
              <svg className="w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9" />
              </svg>
              <span className="text-sm font-medium text-gray-700 dark:text-gray-300">Notification Preferences</span>
            </div>
            <svg className="w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
            </svg>
          </Link>
          <Link to="/settings" className="flex items-center justify-between p-3 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors">
            <div className="flex items-center gap-3">
              <svg className="w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M20.354 15.354A9 9 0 018.646 3.646 9.003 9.003 0 0012 21a9.003 9.003 0 008.354-5.646z" />
              </svg>
              <span className="text-sm font-medium text-gray-700 dark:text-gray-300">Appearance & Dark Mode</span>
            </div>
            <svg className="w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
            </svg>
          </Link>
          <Link to="/settings" className="flex items-center justify-between p-3 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors">
            <div className="flex items-center gap-3">
              <svg className="w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
              </svg>
              <span className="text-sm font-medium text-gray-700 dark:text-gray-300">Change Password</span>
            </div>
            <svg className="w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
            </svg>
          </Link>
          <Link to="/settings" className="flex items-center justify-between p-3 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors">
            <div className="flex items-center gap-3">
              <svg className="w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
              </svg>
              <span className="text-sm font-medium text-gray-700 dark:text-gray-300">Account Information</span>
            </div>
            <svg className="w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
            </svg>
          </Link>
        </div>
      </div>

      {/* Danger Zone - not prominent */}
      <div className="mt-8 pt-8 border-t border-gray-200 dark:border-gray-700">
        <div className="bg-red-50 dark:bg-red-900/20 rounded-xl p-6">
          <h3 className="text-lg font-semibold text-red-900 dark:text-red-300 mb-2">Danger Zone</h3>
          <p className="text-sm text-red-700 dark:text-red-400 mb-4">
            Once you delete your account, there is no going back. Please be certain.
          </p>
          {!showDeleteConfirm ? (
            <button
              onClick={() => setShowDeleteConfirm(true)}
              className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors font-medium text-sm"
            >
              Delete My Account
            </button>
          ) : (
            <div className="space-y-3">
              {deleteStep === 0 && (
                <div>
                  <p className="text-sm text-red-700 dark:text-red-400 mb-2">
                    Are you sure you want to delete your account? This will remove your profile and all associated data.
                  </p>
                  <div className="flex gap-2">
                    <button onClick={handleDeleteAccount} className="px-4 py-2 bg-red-600 text-white rounded-lg text-sm font-medium hover:bg-red-700">
                      Yes, continue
                    </button>
                    <button onClick={() => setShowDeleteConfirm(false)} className="px-4 py-2 bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded-lg text-sm font-medium">
                      Cancel
                    </button>
                  </div>
                </div>
              )}
              {deleteStep === 1 && (
                <div>
                  <p className="text-sm text-red-700 dark:text-red-400 mb-2 font-medium">
                    This is your final warning. All your registrations, saved events, and favorite speakers will be permanently deleted.
                  </p>
                  <div className="flex gap-2">
                    <button onClick={handleDeleteAccount} className="px-4 py-2 bg-red-600 text-white rounded-lg text-sm font-medium hover:bg-red-700">
                      I understand, delete everything
                    </button>
                    <button onClick={() => { setDeleteStep(0); setShowDeleteConfirm(false); }} className="px-4 py-2 bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded-lg text-sm font-medium">
                      Cancel
                    </button>
                  </div>
                </div>
              )}
              {deleteStep === 2 && (
                <div>
                  <p className="text-sm text-red-700 dark:text-red-400 mb-2 font-bold">
                    Last chance — type DELETE to confirm:
                  </p>
                  <div className="flex gap-2">
                    <button onClick={handleDeleteAccount} disabled={saving} className="px-4 py-2 bg-red-700 text-white rounded-lg text-sm font-medium hover:bg-red-800 disabled:opacity-50">
                      {saving ? 'Deleting...' : 'Permanently Delete'}
                    </button>
                    <button onClick={() => { setDeleteStep(0); setShowDeleteConfirm(false); }} className="px-4 py-2 bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded-lg text-sm font-medium">
                      Cancel
                    </button>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default AttendeeProfilePage;
